import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import AgentRegistrationForm from './components/AgentRegistrationForm';
import OnboardingProgress from './components/OnboardingProgress';
import BusinessVerificationPanel from './components/BusinessVerificationPanel';
import TerritorySelection from './components/TerritorySelection';
import CommissionPreview from './components/CommissionPreview';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

const AgentRegistration = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [agentData, setAgentData] = useState({
    agentType: '',
    businessInfo: {},
    contactDetails: {},
    bankingInfo: {},
    documents: {},
    territories: [],
    performanceTargets: {}
  });
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  const totalSteps = 5;

  const handleStepSubmit = async (stepData, step) => {
    setIsLoading(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Update agent data with step information
      setAgentData(prev => ({
        ...prev,
        ...stepData
      }));

      console.log(`Step ${step} data:`, stepData);
      
      if (step < totalSteps) {
        setCurrentStep(step + 1);
      } else {
        // Final submission
        handleFinalSubmission();
      }
      
    } catch (error) {
      console.error('Step submission failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFinalSubmission = async () => {
    setShowSuccessMessage(true);
    
    // Redirect to pending approval status page after showing success message
    setTimeout(() => {
      navigate('/agent-dashboard', { 
        state: { 
          registrationComplete: true,
          agentStatus: 'pending_approval',
          agentData: agentData
        }
      });
    }, 3000);
  };

  const handlePreviousStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleLoginRedirect = () => {
    navigate('/login');
  };

  if (showSuccessMessage) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center min-h-[calc(100vh-4rem)] px-4 py-8">
          <div className="max-w-md w-full text-center space-y-6">
            <div className="w-20 h-20 bg-success/10 rounded-full flex items-center justify-center mx-auto">
              <Icon name="CheckCircle" size={40} className="text-success" />
            </div>
            <div className="space-y-3">
              <h1 className="text-2xl font-bold text-foreground">Application Submitted!</h1>
              <p className="text-muted-foreground">
                Your agent registration application has been successfully submitted. 
                Our compliance team will review your application and documents within 3-5 business days.
              </p>
              <div className="bg-muted/50 rounded-lg p-4 space-y-2">
                <div className="flex items-center justify-center space-x-2 text-sm font-medium text-foreground">
                  <Icon name="Clock" size={16} />
                  <span>Expected Review Timeline</span>
                </div>
                <p className="text-sm text-muted-foreground">3-5 Business Days</p>
              </div>
            </div>
            <div className="flex items-center justify-center space-x-2 text-sm text-muted-foreground">
              <Icon name="ArrowRight" size={16} />
              <span>Redirecting to agent dashboard...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Agent Registration - MoneyTransfer Pro</title>
        <meta name="description" content="Join our money transfer network as an agent or affiliate. Complete registration to start earning commissions on international transfers." />
      </Helmet>
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">
            Join Our Agent Network
          </h1>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Become a certified MoneyTransfer Pro agent and start earning competitive commissions 
            on international money transfers while helping customers with their financial needs.
          </p>
        </div>

        {/* Progress Indicator */}
        <div className="mb-8">
          <OnboardingProgress currentStep={currentStep} totalSteps={totalSteps} />
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Main Registration Form */}
          <div className="lg:col-span-3">
            <div className="bg-card rounded-lg border border-border p-6 lg:p-8">
              {currentStep === 1 && (
                <AgentRegistrationForm
                  onSubmit={(data) => handleStepSubmit(data, 1)}
                  isLoading={isLoading}
                  agentData={agentData}
                />
              )}
              
              {currentStep === 2 && (
                <BusinessVerificationPanel
                  onSubmit={(data) => handleStepSubmit(data, 2)}
                  onPrevious={handlePreviousStep}
                  isLoading={isLoading}
                  agentData={agentData}
                />
              )}
              
              {currentStep === 3 && (
                <TerritorySelection
                  onSubmit={(data) => handleStepSubmit(data, 3)}
                  onPrevious={handlePreviousStep}
                  isLoading={isLoading}
                  agentData={agentData}
                />
              )}
              
              {currentStep === 4 && (
                <CommissionPreview
                  onSubmit={(data) => handleStepSubmit(data, 4)}
                  onPrevious={handlePreviousStep}
                  isLoading={isLoading}
                  agentData={agentData}
                />
              )}

              {currentStep === 5 && (
                <div className="space-y-6">
                  <div className="text-center space-y-4">
                    <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                      <Icon name="FileCheck" size={32} className="text-primary" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-semibold text-foreground mb-2">
                        Review & Submit Application
                      </h2>
                      <p className="text-muted-foreground">
                        Please review all information before submitting your agent application.
                      </p>
                    </div>
                  </div>

                  {/* Application Review Summary */}
                  <div className="bg-muted/30 rounded-lg p-6 space-y-4">
                    <h3 className="text-lg font-semibold text-foreground">Application Summary</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-muted-foreground">Agent Type:</span>
                        <p className="font-medium capitalize">{agentData?.agentType || 'Individual Agent'}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Selected Territories:</span>
                        <p className="font-medium">{agentData?.territories?.length || 0} regions selected</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Business Status:</span>
                        <p className="font-medium">Verification Pending</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Expected Commission:</span>
                        <p className="font-medium">2.5% - 4.0% per transaction</p>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row gap-4 pt-4">
                    <Button
                      variant="outline"
                      size="lg"
                      onClick={handlePreviousStep}
                      iconName="ArrowLeft"
                      iconPosition="left"
                      className="sm:w-auto"
                    >
                      Review Commission Structure
                    </Button>
                    <Button
                      variant="default"
                      size="lg"
                      onClick={() => handleStepSubmit(agentData, 5)}
                      loading={isLoading}
                      iconName="Send"
                      iconPosition="right"
                      className="sm:flex-1"
                    >
                      Submit Application for Review
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Already have account link */}
            {currentStep === 1 && (
              <div className="text-center mt-6">
                <p className="text-sm text-muted-foreground">
                  Already have an agent account?{' '}
                  <Button
                    variant="link"
                    onClick={handleLoginRedirect}
                    className="p-0 h-auto font-medium"
                  >
                    Sign in here
                  </Button>
                </p>
              </div>
            )}
          </div>

          {/* Sidebar - Info Panel */}
          <div className="lg:col-span-1 space-y-6">
            {/* Agent Benefits */}
            <div className="bg-card rounded-lg border border-border p-6">
              <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center space-x-2">
                <Icon name="Star" size={20} className="text-primary" />
                <span>Agent Benefits</span>
              </h3>
              <ul className="space-y-3 text-sm">
                <li className="flex items-start space-x-2">
                  <Icon name="Check" size={16} className="text-success mt-0.5" />
                  <span className="text-muted-foreground">Competitive commission rates up to 4%</span>
                </li>
                <li className="flex items-start space-x-2">
                  <Icon name="Check" size={16} className="text-success mt-0.5" />
                  <span className="text-muted-foreground">Weekly commission payouts</span>
                </li>
                <li className="flex items-start space-x-2">
                  <Icon name="Check" size={16} className="text-success mt-0.5" />
                  <span className="text-muted-foreground">Dedicated agent support team</span>
                </li>
                <li className="flex items-start space-x-2">
                  <Icon name="Check" size={16} className="text-success mt-0.5" />
                  <span className="text-muted-foreground">Marketing materials provided</span>
                </li>
                <li className="flex items-start space-x-2">
                  <Icon name="Check" size={16} className="text-success mt-0.5" />
                  <span className="text-muted-foreground">Performance bonuses available</span>
                </li>
                <li className="flex items-start space-x-2">
                  <Icon name="Check" size={16} className="text-success mt-0.5" />
                  <span className="text-muted-foreground">Territory expansion opportunities</span>
                </li>
              </ul>
            </div>

            {/* Required Documents */}
            <div className="bg-card rounded-lg border border-border p-6">
              <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center space-x-2">
                <Icon name="FileText" size={20} className="text-primary" />
                <span>Required Documents</span>
              </h3>
              <ul className="space-y-3 text-sm">
                <li className="flex items-start space-x-2">
                  <Icon name="File" size={16} className="text-muted-foreground mt-0.5" />
                  <span className="text-muted-foreground">Government-issued ID</span>
                </li>
                <li className="flex items-start space-x-2">
                  <Icon name="File" size={16} className="text-muted-foreground mt-0.5" />
                  <span className="text-muted-foreground">Business registration (if applicable)</span>
                </li>
                <li className="flex items-start space-x-2">
                  <Icon name="File" size={16} className="text-muted-foreground mt-0.5" />
                  <span className="text-muted-foreground">Tax identification number</span>
                </li>
                <li className="flex items-start space-x-2">
                  <Icon name="File" size={16} className="text-muted-foreground mt-0.5" />
                  <span className="text-muted-foreground">Banking information</span>
                </li>
                <li className="flex items-start space-x-2">
                  <Icon name="File" size={16} className="text-muted-foreground mt-0.5" />
                  <span className="text-muted-foreground">Operating license (business agents)</span>
                </li>
              </ul>
            </div>

            {/* Support Contact */}
            <div className="bg-gradient-to-br from-primary/5 to-primary/10 rounded-lg border border-primary/20 p-6">
              <h3 className="text-lg font-semibold text-foreground mb-3 flex items-center space-x-2">
                <Icon name="Headphones" size={20} className="text-primary" />
                <span>Need Help?</span>
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                Our agent onboarding specialists are available to assist you through the registration process.
              </p>
              <Button
                variant="outline"
                size="sm"
                fullWidth
                iconName="Phone"
                iconPosition="left"
              >
                Contact Support
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentRegistration;